import { useQuery } from '@tanstack/react-query';
import { fetchSearch, type FetchSearchOptions, type SearchGroup } from '@/lib/search';
import { useDebouncedValue } from './useDebouncedValue';

const DEBOUNCE_MS = 150;

export interface UseSearchResult {
  /** The debounced query actually sent to the server. */
  query: string;
  groups: SearchGroup[];
  /** True only while an enabled (non-empty) query is in flight with no data yet. */
  isLoading: boolean;
  isError: boolean;
}

/**
 * Central search hook (replit.md §0.8 + §1.4 — one shared query layer). Debounces
 * the input and reads through the shared query key `['search', query]` so every
 * caller of the same term hits one cache entry. Disabled (no fetch) until there
 * is a non-empty query.
 */
export function useSearch(rawQuery: string, options?: FetchSearchOptions): UseSearchResult {
  const query = useDebouncedValue(rawQuery.trim(), DEBOUNCE_MS);
  const enabled = query.length > 0;

  const result = useQuery({
    queryKey: ['search', query],
    queryFn: () => fetchSearch(query, options),
    enabled,
  });

  return {
    query,
    groups: result.data?.groups ?? [],
    // react-query reports `pending` even when disabled; gate on `enabled`.
    isLoading: enabled && result.isPending,
    isError: result.isError,
  };
}
